export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

// Largest axis-aligned rectangle inside `bounds` that intersects none of the
// `obstacles`. Used to find the part of the map not covered by floating UI
// cards, so fits/centers can aim there.
//
// A per-obstacle "which edge does this card hug" heuristic is not enough: on
// phones the trail dock floats just above the bottom sheet, i.e. almost
// exactly mid-map, so no single edge assignment describes it - the free area
// only falls out of considering all the cards together. Candidate rectangles
// are spanned by the obstacle edges (plus the bounds), so the optimum is
// always on the grid; with at most a handful of obstacles the O(n^4) sweep
// is a few hundred cheap checks.
//
// If the obstacles cover everything, the returned rectangle has zero area.
export function largestFreeRect(bounds: Rect, obstacles: Rect[]): Rect {
  const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
  const cards = obstacles
    .map((o) => ({
      left: clamp(o.left, bounds.left, bounds.right),
      right: clamp(o.right, bounds.left, bounds.right),
      top: clamp(o.top, bounds.top, bounds.bottom),
      bottom: clamp(o.bottom, bounds.top, bounds.bottom),
    }))
    .filter((o) => o.right > o.left && o.bottom > o.top);
  if (cards.length === 0) return { ...bounds };

  const xs = [...new Set([bounds.left, bounds.right, ...cards.flatMap((c) => [c.left, c.right])])].sort(
    (a, b) => a - b,
  );
  const ys = [...new Set([bounds.top, bounds.bottom, ...cards.flatMap((c) => [c.top, c.bottom])])].sort(
    (a, b) => a - b,
  );

  let best: Rect = { left: bounds.left, top: bounds.top, right: bounds.left, bottom: bounds.top };
  let bestArea = 0;
  for (let i = 0; i < xs.length - 1; i++) {
    for (let j = i + 1; j < xs.length; j++) {
      for (let k = 0; k < ys.length - 1; k++) {
        for (let l = k + 1; l < ys.length; l++) {
          const area = (xs[j] - xs[i]) * (ys[l] - ys[k]);
          if (area <= bestArea) continue;
          const blocked = cards.some(
            (c) => c.left < xs[j] && c.right > xs[i] && c.top < ys[l] && c.bottom > ys[k],
          );
          if (!blocked) {
            best = { left: xs[i], top: ys[k], right: xs[j], bottom: ys[l] };
            bestArea = area;
          }
        }
      }
    }
  }
  return best;
}
